/**
 * Public landing page.
 *
 * Business rules:
 * - Must be accessible without authentication (marketing entry point).
 * - "Sign in" button routes to `/auth/signin` for existing users.
 * - "Start for Free" buttons route to `/auth/signup` for new users.
 * - Onboarded users (PBI #1102) see Go to Overview instead of signup CTAs.
 * - Showcases system card templates via the public API (no auth required).
 * - Clicking a template opens a preview modal with guest scheduling flow.
 * - Seasonal banner sits between nav and hero when the public API returns one.
 *   Banner JSON is prefetched at chunk load and raced with landing-lcp so the
 *   tinted shell is not blocked on the heavier LCP payload (Task #1067).
 *
 * Section order:
 * [Nav] -> [Seasonal Banner?] -> [Hero] -> [Explore Cards] -> [Compact How It Works]
 * -> [Differentiation] -> [Trust Strip] -> [Social Proof] -> [Occasion Reminder]
 * -> [Mini-FAQ] -> [Final CTA] -> [Coming Soon] -> [Footer]
 *
 * Messaging strategy:
 * - "Peace of Mind Guaranteed" pill badge above the headline.
 * - Pain-first headline: "Never forget another [birthday / anniversary / ...]."
 * - Rotating occasion word — allowlisted names only; static H1 in DOM for SEO.
 * - Subheadline: benefit line + category line + highlight chips (PBI #1238).
 * - Hero image shows relaxed person with rotating status badge overlay.
 * - Cards section immediately after hero to demonstrate product quality.
 * - Compact How It Works strip and mini-FAQ stay for conversion; full versions on /how-it-works and /faq.
 * - Alternating section backgrounds for visual rhythm (no dividers).
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
  rem,
} from '@mantine/core';
import { IconAlertCircle, IconGift } from '@tabler/icons-react';
import { landingContent } from '../content/landingContent';
import {
  brandColors,
  commonStyles,
  semanticColors,
} from '../config/theme';
import { HeroCopyStack } from '../components/landing/HeroCopyStack';
import { PUBLIC_ROUTES, APP_ROUTES } from '../constants/routes';
import { SIGNUP_CTA_IDS } from '../constants/signupCtaIds';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePublicAuthedNav } from '../hooks/usePublicAuthedNav';
import { usePublicSubscriptionConfig } from '../hooks/usePublicSubscriptionConfig';
import { beginSignup } from '../services/beginSignup';
import { consumeAccountDisabledBanner } from '../services/accountDisabledFlow';
import { FaqJsonLd } from '../components/seo/FaqJsonLd';
import { flattenUniqueFaqItems, mapFaqItemsWithPricing } from '../utils/resolveFaqWithPricing';
import {
  getPublicEventTypes,
  getPublicLandingLcp,
  getPublicSeasonalBanner,
  resolvePublicImageUrl,
} from '../services/publicTemplatesService';
import type { PublicSeasonalBanner } from '../services/publicTemplatesService';
import { getSubscriptionPlans } from '../services/subscriptionService';
import type { RotatorWord } from '../components/landing/HeadlineRotator';
import {
  TrustStripSection,
  ComingSoonSection,
  OccasionReminderSection,
  SocialProofSection,
  HeadlineRotator,
  HeroImageWithBadge,
  PublicNav,
  PublicFooter,
  SeasonalBannerSection,
  LANDING_EXPLORE_SECTION_ID,
} from '../components/landing';
// File-path import — keeps ExploreCardsSection out of the landing barrel graph (PBI #1144).
import { LazyExploreCardsSection } from '../components/landing/LazyExploreCardsSection';

/**
 * Start the light seasonal-banner fetch as soon as this chunk evaluates —
 * before React mounts / useEffect — so a hard reload races the request with
 * JS parse time instead of waiting for the effect queue (Task #1067).
 */
const seasonalBannerPrefetch = getPublicSeasonalBanner();

/**
 * Warms the browser HTTP cache for the banner artwork once JSON is known.
 */
function prefetchSeasonalBannerImage(banner: PublicSeasonalBanner | null): void {
  const raw = banner?.imageUrl?.trim();
  if (!raw || typeof Image === 'undefined') return;

  const img = new Image();
  img.decoding = 'async';
  img.src = resolvePublicImageUrl(raw);
}

/**
 * Accent colors cycled through for each rotator word.
 * Keeps visual variety as the number of event types grows.
 */
const ROTATOR_COLORS = [
  brandColors.indigoText,
  brandColors.tealText,
  brandColors.amberText,
] as const;

/** Number of FAQ items to show in the mini-FAQ on the landing page */
const MINI_FAQ_COUNT = 3;

/**
 * Filters API event type names to the marketing allowlist (case-insensitive).
 *
 * @param names Event type display names from GET /public/event-types.
 * @returns Allowlisted names in API order, or empty when none match.
 */
function filterRotatorNames(names: string[]): string[] {
  const allow = new Set(
    landingContent.HEADLINE_ROTATOR_ALLOWLIST.map((n) => n.toLowerCase()),
  );

  return names.filter((name) => allow.has(name.trim().toLowerCase()));
}

/**
 * Formats the Personal plan monthly price for the final CTA microcopy.
 *
 * @param monthlyPriceCents Plan monthly price in cents.
 */
function formatPersonalPlanAnchor(monthlyPriceCents: number): string {
  const dollars = (monthlyPriceCents / 100).toFixed(monthlyPriceCents % 100 === 0 ? 0 : 2);
  return `Personal plan from $${dollars}/mo`;
}

/**
 * Landing page — leads with the "never forget" scheduling value proposition,
 * shows cards immediately, then builds trust through compact how-it-works,
 * differentiation, social proof, and a mini-FAQ.
 */
export const LandingPage = () => {
  const navigate = useNavigate();
  usePageMeta(landingContent.meta.landing);
  const { retargetSignupCta } = usePublicAuthedNav();
  const { config: publicSubscriptionConfig } = usePublicSubscriptionConfig();

  const [eventTypeNames, setEventTypeNames] = useState<string[]>([]);
  const [seasonalBanner, setSeasonalBanner] = useState<PublicSeasonalBanner | null | undefined>(
    undefined,
  );
  const [priceAnchor, setPriceAnchor] = useState<string>(landingContent.cta.priceAnchorFallback);
  const [accountDisabledBanner, setAccountDisabledBanner] = useState<string | null>(null);

  const miniFaqItems = mapFaqItemsWithPricing(
    landingContent.faq.slice(0, MINI_FAQ_COUNT),
    publicSubscriptionConfig,
  );

  useEffect(() => {
    setAccountDisabledBanner(consumeAccountDisabledBanner());
  }, []);

  useEffect(() => {
    getPublicEventTypes()
      .then((types) => setEventTypeNames(types.map((t) => t.name)))
      .catch(() => {
        // Silently fall back to static words on failure
      });
  }, []);

  // Race the module-level seasonal-banner prefetch with landing-lcp so the
  // tinted shell can paint without waiting on carousel LCP work.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const applyBanner = (banner: PublicSeasonalBanner | null) => {
      if (cancelled) return;
      setSeasonalBanner(banner);
      prefetchSeasonalBannerImage(banner);
    };

    // Paint as soon as the light endpoint (started at chunk load) answers.
    void seasonalBannerPrefetch.then((banner) => {
      if (cancelled) return;
      setSeasonalBanner((prev) => {
        if (prev !== undefined) return prev;
        prefetchSeasonalBannerImage(banner);
        return banner;
      });
    });

    void getPublicLandingLcp({ signal: controller.signal })
      .then((lcp) => {
        if (cancelled) return;

        if (lcp && 'seasonalBanner' in lcp) {
          applyBanner(lcp.seasonalBanner ?? null);
          return;
        }

        // LCP omitted the field — settle from the dedicated prefetch.
        return seasonalBannerPrefetch.then(applyBanner);
      })
      .catch(() => {
        return seasonalBannerPrefetch.then(applyBanner).catch(() => {
          if (!cancelled) setSeasonalBanner(null);
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    getSubscriptionPlans()
      .then((plans) => {
        const personal = plans[0];
        if (personal?.monthlyPriceCents != null) {
          setPriceAnchor(formatPersonalPlanAnchor(personal.monthlyPriceCents));
        }
      })
      .catch(() => {
        // Keep fallback microcopy on error
      });
  }, []);

  /**
   * Builds rotator words from allowlisted backend event types.
   * Falls back to landingContent.brand.headlineRotatorWords when none match.
   */
  const rotatorWords: readonly RotatorWord[] = useMemo(() => {
    const filtered = filterRotatorNames(eventTypeNames);
    if (filtered.length === 0) {
      return landingContent.brand.headlineRotatorWords;
    }
    return filtered.map((name, idx) => ({
      text: `${name.toLowerCase()}.`,
      color: ROTATOR_COLORS[idx % ROTATOR_COLORS.length],
    }));
  }, [eventTypeNames]);

  /**
   * Builds the SEO H1 from allowlisted event type names (or static fallback).
   */
  const seoH1 = useMemo(() => {
    const filtered = filterRotatorNames(eventTypeNames);
    if (filtered.length === 0) {
      return landingContent.brand.headlineH1;
    }
    const lowered = filtered.map((n) => n.toLowerCase());
    if (lowered.length === 1) {
      return `${landingContent.brand.headlineTemplate} ${lowered[0]}.`;
    }
    const allButLast = lowered.slice(0, -1).join(', ');
    const last = lowered[lowered.length - 1];
    return `${landingContent.brand.headlineTemplate} ${allButLast}, or ${last}.`;
  }, [eventTypeNames]);

  const handleGetStarted = (ctaId: typeof SIGNUP_CTA_IDS.LandingHero | typeof SIGNUP_CTA_IDS.LandingFinal) => {
    if (retargetSignupCta) {
      navigate(APP_ROUTES.overview);
      return;
    }

    beginSignup(navigate, ctaId);
  };

  return (
    <Box style={{ ...commonStyles.warmPageBackground, position: 'relative' }}>

      {/* ================================================================
          NAV BAR — indigo-tinted background to separate from hero
          ================================================================ */}
      <Box style={{ background: semanticColors.bg.navBar, position: 'relative', zIndex: 1 }}>
        <Container size="lg" py={rem(14)}>
          <PublicNav />
        </Container>
      </Box>

      {accountDisabledBanner && (
        <Container size="lg" pt="md">
          <Alert
            color={brandColors.danger}
            icon={<IconAlertCircle size={18} />}
            title="Account disabled"
            withCloseButton
            onClose={() => setAccountDisabledBanner(null)}
          >
            {accountDisabledBanner}
          </Alert>
        </Container>
      )}

      {/* Seasonal marketing strip — no reserved space when absent */}
      <SeasonalBannerSection banner={seasonalBanner} />

      {/* ================================================================
          HERO SECTION — pain-first headline + peace-of-mind image
          ================================================================ */}
      <Container size="lg" style={{ position: 'relative', zIndex: 1 }}>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" mt={rem(20)} mb={rem(48)}>
          <Stack gap="lg" justify="center">
            {/* Peace of Mind pill */}
            <Badge variant="light" color={brandColors.scheduling} size="lg" radius="xl" style={{ alignSelf: 'flex-start' }}>
              {landingContent.heroPill}
            </Badge>

            {/* Rotating headline — "Never forget another [birthday]." */}
            <HeadlineRotator
              prefix={landingContent.brand.headlineTemplate}
              words={rotatorWords}
              seoH1={seoH1}
            />

            {/* Positioning → products → highlight chips (PBI #1238) */}
            <HeroCopyStack />

            {/* Primary CTA + "Includes Free E-Cards" badge (badge only for signup) */}
            <Group gap="md" align="center">
              <Button size="md" color={brandColors.primary} onClick={() => handleGetStarted(SIGNUP_CTA_IDS.LandingHero)}>
                {retargetSignupCta ? landingContent.cta.goToOverview : landingContent.cta.primary}
              </Button>
              {!retargetSignupCta && (
                <Group gap={6} wrap="nowrap">
                  <IconGift size={18} color="var(--mantine-color-indigo-6)" />
                  <Text size="sm" c={semanticColors.text.secondary}>
                    {landingContent.cta.badge}
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>

          {/* Hero image with rotating status badge */}
          <Stack align="center" justify="center">
            <HeroImageWithBadge />
          </Stack>
        </SimpleGrid>
      </Container>

      {/* ================================================================
          EXPLORE CARDS — template gallery, right after hero
          ================================================================ */}
      <Box id={LANDING_EXPLORE_SECTION_ID} py={rem(48)} style={{ background: '#FFFFFF' }}>
        <Container size="lg">
          <LazyExploreCardsSection />
        </Container>
      </Box>

      {/* ================================================================
          COMPACT HOW IT WORKS — 3-icon strip with "Learn more" link
          ================================================================ */}
      <Box py={rem(48)} style={{ background: '#EEF2FF' }}>
        <Container size="lg">
          <Title order={2} ta="center" c={semanticColors.text.title} mb="xl">
            {landingContent.sections.howItWorks.title}
          </Title>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
            {landingContent.sections.howItWorks.steps.map((step, idx) => (
              <Stack key={step.title} align="center" gap="sm">
                <ThemeIcon size={48} radius="xl" color={brandColors.primary} variant="light">
                  <Text fw={700} size="lg">{idx + 1}</Text>
                </ThemeIcon>
                <Text fw={600} ta="center" c={semanticColors.text.heading} size="sm">
                  {step.title.replace(/^\d+\.\s*/, '')}
                </Text>
                <Text size="xs" c={semanticColors.text.secondary} ta="center" style={{ maxWidth: 260 }}>
                  {step.description}
                </Text>
              </Stack>
            ))}
          </SimpleGrid>

          <Group justify="center" mt="lg">
            <UnstyledButton component={Link} to={PUBLIC_ROUTES.howItWorks}>
              <Text size="sm" c={brandColors.indigoText} fw={600}>
                See how it works &rarr;
              </Text>
            </UnstyledButton>
          </Group>
        </Container>
      </Box>

      {/* ================================================================
          DIFFERENTIATION — why not just a calendar reminder?
          ================================================================ */}
      <Box py={rem(48)} style={{ background: semanticColors.bg.section }}>
        <Container size="lg">
          <Title order={2} ta="center" c={semanticColors.text.title} mb="xl">
            {landingContent.sections.differentiation.title}
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
            {landingContent.sections.differentiation.points.map((point) => (
              <Stack key={point.title} gap="xs">
                <Text fw={600} c={semanticColors.text.heading} size="sm">
                  {point.title}
                </Text>
                <Text size="sm" c={semanticColors.text.secondary}>
                  {point.description}
                </Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ================================================================
          TRUST STRIP
          ================================================================ */}
      <Box py={rem(32)}>
        <Container size="lg">
          <TrustStripSection />
        </Container>
      </Box>

      {/* ================================================================
          SOCIAL PROOF — aspirational scenarios
          ================================================================ */}
      <Box py={rem(48)} style={{ background: '#F0FDFA' }}>
        <Container size="lg">
          <SocialProofSection />
        </Container>
      </Box>

      {/* ================================================================
          OCCASION REMINDER — free yearly lead magnet (PBI #1066)
          ================================================================ */}
      <Box py={rem(48)} style={{ background: '#FFF7ED' }}>
        <Container size="lg">
          <OccasionReminderSection />
        </Container>
      </Box>

      {/* Runtime FAQ JSON-LD for exactly the mini-FAQ items rendered below (PBI #1193, #1270). */}
      <FaqJsonLd items={flattenUniqueFaqItems([miniFaqItems])} pagePath={PUBLIC_ROUTES.home} />

      {/* ================================================================
          MINI-FAQ — top 3 conversion-critical questions
          ================================================================ */}
      <Box py={rem(48)} style={{ background: '#FFFFFF' }}>
        <Container size="md">
          <Title order={2} ta="center" c={semanticColors.text.title} mb="lg">
            Common questions
          </Title>
          <Accordion variant="separated" radius="md">
            {miniFaqItems.map((item) => {
              const question = item.q ?? '';
              return (
                <Accordion.Item key={question} value={question}>
                  <Accordion.Control>{question}</Accordion.Control>
                  <Accordion.Panel>
                    <Text c={semanticColors.text.secondary}>{item.a}</Text>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
          <Group justify="center" mt="lg">
            <UnstyledButton component={Link} to={PUBLIC_ROUTES.faq}>
              <Text size="sm" c={brandColors.indigoText} fw={600}>
                See all questions &rarr;
              </Text>
            </UnstyledButton>
          </Group>
        </Container>
      </Box>

      {/* ================================================================
          FINAL CTA — with Personal plan price anchor
          ================================================================ */}
      <Box py={rem(32)}>
        <Container size="lg">
          <Box
            p={rem(32)}
            mb={rem(32)}
            style={{
              ...commonStyles.accentBox,
              textAlign: 'center',
              borderRadius: 'var(--mantine-radius-lg)',
            }}
          >
            <Title order={2} c={semanticColors.text.title}>{landingContent.sections.finalCta.title}</Title>
            <Text c={semanticColors.text.body} mt="xs">
              {landingContent.sections.finalCta.subtitle}
            </Text>

            <Button
              size="lg"
              color={brandColors.primary}
              mt="lg"
              onClick={() => handleGetStarted(SIGNUP_CTA_IDS.LandingFinal)}
            >
              {retargetSignupCta ? landingContent.cta.goToOverview : landingContent.cta.primary}
            </Button>

            {!retargetSignupCta && (
              <>
                <Text size="sm" c={semanticColors.text.subtle} mt="sm">
                  {landingContent.cta.microcopy}
                </Text>
                <Text size="sm" c={semanticColors.text.subtle} mt={4}>
                  {priceAnchor}
                </Text>
              </>
            )}
          </Box>
        </Container>
      </Box>

      {/* ================================================================
          COMING SOON — below final CTA (PBI #1064)
          ================================================================ */}
      <Box py={rem(48)} style={{ background: 'var(--mantine-color-indigo-9)' }}>
        <Container size="lg">
          <ComingSoonSection dark />
        </Container>
      </Box>

      {/* ================================================================
          FOOTER
          ================================================================ */}
      <Container size="lg" py={rem(20)}>
        <PublicFooter />
      </Container>
    </Box>
  );
};
